import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  NucleusUser,
  NucleusUserFilter,
  NucleusUserPage,
  NucleusUserPageResult,
  NucleusUserServiceContract,
} from '@nucleus/user-ui';
import { ServiceHandler, Page } from '@nucleus/ui-base';
import { UserService } from '../user/user.service';
import { UserFilter } from '../user/user-filter';
import { FilterRequest } from '../filter-request';
import {
  User,
  BlockUserPayload,
  UnblockUserPayload,
  ResetPasswordPayload,
} from '@nucleus/user-ui';

/**
 * Tasksense-side adapter — bridges the callback-style UserService to the
 * Observable contract @nucleus/user-ui expects. Mirrors bookwise + goldfish
 * patterns. Tenant payload types (CreateUserPayload /
 * UpdateUserPayload) pass through as `unknown` — the lib never
 * inspects them and forwards verbatim from buildCreatePayload / buildUpdatePayload.
 */
@Injectable({ providedIn: 'root' })
export class TasksenseUserServiceAdapter implements NucleusUserServiceContract {
  private readonly userService = inject(UserService);

  filter(filter: NucleusUserFilter, page: NucleusUserPage): Observable<NucleusUserPageResult> {
    const tenantFilter = this.toTenantFilter(filter);
    const tenantPage = this.toTenantPage(page);
    return this.bridge<any, NucleusUserPageResult>(
      (handler) => this.userService.filter(handler, tenantFilter, tenantPage),
      (result) => ({
        results: (result?.data ?? []) as NucleusUser[],
        totalRecords: result?.totalData ?? 0,
      }),
    );
  }

  getByUsername(username: string): Observable<NucleusUser> {
    return this.bridge<User, NucleusUser>(
      (handler) => this.userService.findByUsername(username, handler),
      (u) => u as unknown as NucleusUser,
    );
  }

  create(payload: unknown): Observable<NucleusUser> {
    return this.bridge<User, NucleusUser>(
      (handler) => this.userService.create(payload as any, handler),
      (u) => u as unknown as NucleusUser,
    );
  }

  update(payload: unknown): Observable<NucleusUser> {
    return this.bridge<User, NucleusUser>(
      (handler) => this.userService.update(payload as any, handler),
      (u) => u as unknown as NucleusUser,
    );
  }

  block(username: string): Observable<NucleusUser> {
    const body: BlockUserPayload = { username };
    return this.bridge<User, NucleusUser>(
      (handler) => this.userService.block(body, handler),
      (u) => u as unknown as NucleusUser,
    );
  }

  unblock(username: string): Observable<NucleusUser> {
    const body: UnblockUserPayload = { username };
    return this.bridge<User, NucleusUser>(
      (handler) => this.userService.unblock(body, handler),
      (u) => u as unknown as NucleusUser,
    );
  }

  resetPassword(username: string): Observable<NucleusUser> {
    const body: ResetPasswordPayload = { userId: username };
    return this.bridge<User, NucleusUser>(
      (handler) => this.userService.resetPassword(body, handler),
      (u) => u as unknown as NucleusUser,
    );
  }

  softDelete(username: string): Observable<void> {
    return this.bridge<void, void>(
      (handler) => this.userService.softDelete(username, handler),
      () => undefined,
    );
  }

  restore(username: string): Observable<void> {
    return this.bridge<void, void>(
      (handler) => this.userService.restore(username, handler),
      () => undefined,
    );
  }

  getPDFReport(filter: NucleusUserFilter, page: NucleusUserPage): Observable<Blob> {
    return this.bridge<Blob, Blob>(
      (handler) => this.userService.getPDFReport(handler, this.toReportRequest(filter, page)),
      (b) => b,
    );
  }

  getXLSReport(filter: NucleusUserFilter, page: NucleusUserPage): Observable<Blob> {
    return this.bridge<Blob, Blob>(
      (handler) => this.userService.getXLSReport(handler, this.toReportRequest(filter, page)),
      (b) => b,
    );
  }

  getCSVReport(filter: NucleusUserFilter, page: NucleusUserPage): Observable<Blob> {
    return this.bridge<Blob, Blob>(
      (handler) => this.userService.getCSVReport(handler, this.toReportRequest(filter, page)),
      (b) => b,
    );
  }

  // ───────────────────────────────────────────────────────────────────────────

  private toTenantFilter(filter: NucleusUserFilter): UserFilter {
    return new UserFilter(
      (filter.status ?? 'ALL') as 'ALL' | 'ACTIVE' | 'BLOCKED',
      filter.permissions ?? [],
      !!filter.includeDeleted,
      filter.value ?? '',
    );
  }

  private toTenantPage(page: NucleusUserPage): Page {
    const p = new Page();
    p.pageNumber = page.pageNumber;
    p.pageSize = page.pageSize;
    p.sortField = page.sortField;
    p.sortDirection = page.sortDirection as any;
    return p;
  }

  private toReportRequest(filter: NucleusUserFilter, page: NucleusUserPage): FilterRequest<UserFilter> {
    const req = new FilterRequest<UserFilter>(this.toTenantFilter(filter));
    req.page = this.toTenantPage(page);
    return req;
  }

  /** Adapter primitive: wraps a callback-style ServiceHandler call in an
   *  Observable. Emits the mapped value on success, errors on failure. */
  private bridge<T, R>(
    invoke: (handler: ServiceHandler<T>) => void,
    map: (value: T) => R,
  ): Observable<R> {
    const subject = new Subject<R>();
    invoke({
      onStart: () => {},
      onSuccess: (value: T) => {
        try {
          subject.next(map(value));
          subject.complete();
        } catch (err) {
          subject.error(err);
        }
      },
      onError: (error: any) => subject.error(error),
      onComplete: () => {},
    });
    return subject.asObservable();
  }
}
